import React, { useEffect, useState } from 'react';
import { Loader2, Mail, Printer, Send, XCircle } from 'lucide-react';
import { toast } from 'sonner';
import { authFetch } from './MedicationExtras';
import { withChartDefaults, cms485Defaults } from '../utils/documentExport';

const ChannelTabs = ({ channel, setChannel, liveFax }) => (
  <div className="grid grid-cols-2 gap-2" data-testid="signature-channel">
    {[['email', 'Email link', Mail], ['fax', liveFax ? 'Fax' : 'Fax (demo)', Printer]].map(([id, label, Icon]) => (
      <button key={id} type="button" onClick={() => setChannel(id)} data-testid={`signature-channel-${id}`}
        className={`py-2 rounded-lg border text-sm font-medium flex items-center justify-center gap-2 transition-colors ${channel === id ? 'bg-gray-900 text-[#d4af37] border-gray-900' : 'text-gray-700 hover:bg-gray-50'}`}>
        <Icon className="w-4 h-4" /> {label}
      </button>
    ))}
  </div>
);

export const SendForSignatureModal = ({ docData, patientContext, onClose, sourceFormId }) => {
  useEffect(() => {
    const esc = (e) => { if (e.key === 'Escape') onClose(); };
    window.addEventListener('keydown', esc);
    return () => window.removeEventListener('keydown', esc);
  }, [onClose]);
  const chart = patientContext?.chart || {};
  const [name, setName] = useState(docData?.data?.physicianName || chart.primaryPhysician || chart.physicianName || chart.physicianSigningName || patientContext?.primaryPhysician || '');
  const [email, setEmail] = useState(chart.physicianEmail || '');
  const [fax, setFax] = useState(chart.physicianFax || '');
  const [faxCfg, setFaxCfg] = useState({ live: false, sender: '' });
  useEffect(() => { authFetch('/fax/config').then(r => r.json()).then(d => d.success && setFaxCfg(d)).catch(() => {}); }, []);
  const [channel, setChannel] = useState('email');
  const [save, setSave] = useState(true);
  const [sending, setSending] = useState(false);
  const [contact, setContact] = useState(null);

  useEffect(() => {
    if (!patientContext?.patientId) return;
    authFetch(`/physician-contact/${patientContext.patientId}`).then(r => (r.ok ? r.json() : null)).then(c => {
      if (!c) return;
      setContact(c);
      setName(n => n || c.name || '');
      setEmail(e => e || c.email || '');
      setFax(f => f || c.fax || '');
      if (!c.email && !chart.physicianEmail && c.fax) setChannel('fax');
    }).catch(() => {});
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [patientContext?.patientId]);

  const send = async () => {
    setSending(true);
    const isFax = channel === 'fax';
    try {
      const res = await authFetch(isFax ? '/esign/485/fax' : '/esign/485/send', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          patientId: patientContext.patientId,
          physicianName: name,
          ...(isFax ? { faxNumber: fax, savePhysicianFax: save } : { physicianEmail: email, savePhysicianEmail: save }),
          sourceFormId: sourceFormId || undefined,
          data: withChartDefaults(docData?.data, cms485Defaults(patientContext)),
        }),
      });
      const data = await res.json().catch(() => ({}));
      if (!res.ok) {
        const msg = typeof data.detail === 'string' ? data.detail
          : res.status === 422 ? `Please check the ${isFax ? 'fax number' : 'email address'}` : 'Service busy, please try again in a minute';
        throw new Error(msg);
      }
      toast.success(data.message, { duration: isFax ? 8000 : 4000 });
      onClose();
    } catch (e) {
      toast.error(`Could not send: ${e.message}`);
    } finally {
      setSending(false);
    }
  };

  const ready = name.trim() && (channel === 'fax' ? fax.replace(/\D/g, '').length >= 10 : email.includes('@'));
  return (
    <div className="fixed inset-0 bg-black/60 flex items-center justify-center z-[100] p-4" data-testid="send-signature-modal">
      <div className="bg-white rounded-xl shadow-2xl max-w-md w-full">
        <div className="bg-gray-900 p-4 rounded-t-xl flex items-center justify-between">
          <h3 className="text-lg font-bold text-[#d4af37]">Send 485 for Physician Signature</h3>
          <button onClick={onClose} className="text-white/80 hover:text-white" data-testid="send-signature-close"><XCircle className="w-5 h-5" /></button>
        </div>
        <div className="p-5 space-y-3">
          <p className="text-sm text-gray-600">Patient: <strong>{patientContext.patientName}</strong>. The physician gets a secure one-time link (valid 14 days) to review and sign box 27. The signed copy is saved to the patient's Application Packages.</p>
          <ChannelTabs channel={channel} setChannel={setChannel} liveFax={faxCfg.live} />
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Physician name</label>
            <input value={name} onChange={(e) => setName(e.target.value)} className="w-full px-3 py-2 border rounded-lg" data-testid="signature-physician-name" />
          </div>
          {channel === 'email' ? (
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Physician email</label>
              <input type="email" value={email} onChange={(e) => setEmail(e.target.value)} className="w-full px-3 py-2 border rounded-lg" placeholder="doctor@clinic.com" data-testid="signature-physician-email" />
            </div>
          ) : (
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Physician fax number</label>
              <input value={fax} onChange={(e) => setFax(e.target.value)} className="w-full px-3 py-2 border rounded-lg" placeholder="(555) 123-4567" data-testid="signature-physician-fax-input" />
              {faxCfg.live
                ? <p className="text-xs text-green-700 mt-1" data-testid="signature-fax-live-note">Sent through RingCentral from {faxCfg.sender || 'the agency fax line'}, with the 485 and the signing link on the cover page.</p>
                : <p className="text-xs text-amber-700 mt-1" data-testid="signature-fax-demo-note">DEMO: the fax is logged with the 485 and signing link, but not really sent until RingCentral is connected.</p>}
            </div>
          )}
          {contact && (
            <div className="text-xs rounded-lg border px-3 py-2 bg-gray-50 space-y-0.5" data-testid="signature-contact-info">
              {contact.email ? <p>Email found in the {contact.sources?.email}.</p>
                : <p className="text-amber-700" data-testid="signature-no-email">No physician email found in the chart or uploaded summaries.{contact.fax ? ' Fax was selected automatically.' : ' Type it above; it will be saved to the patient.'}</p>}
              {contact.fax && <p data-testid="signature-physician-fax">Fax: <strong>{contact.fax}</strong> ({contact.sources?.fax})</p>}
              {contact.phone && <p data-testid="signature-physician-phone">Phone: <strong>{contact.phone}</strong> ({contact.sources?.phone})</p>}
            </div>
          )}
          <label className="flex items-center gap-2 text-sm text-gray-700">
            <input type="checkbox" checked={save} onChange={(e) => setSave(e.target.checked)} data-testid="signature-save-email" />
            Save this {channel === 'fax' ? 'fax number' : 'email'} to the patient for next time
          </label>
          <button onClick={send} disabled={sending || !ready} data-testid="signature-send-btn"
            className="w-full py-2.5 rounded-full bg-[#d4af37] text-gray-900 font-semibold disabled:bg-gray-300 disabled:text-gray-600 flex items-center justify-center gap-2">
            {sending ? <Loader2 className="w-4 h-4 animate-spin" /> : <Send className="w-4 h-4" />} {channel === 'fax' ? `Fax Signature Request${faxCfg.live ? '' : ' (demo)'}` : 'Send Signature Request'}
          </button>
        </div>
      </div>
    </div>
  );
};
